import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, XCircle, Sparkles, AlertCircle, Map } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { checkDuplicateVideo } from "@/lib/youtubeHelpers";
import { getOrCreateCategoryByName } from "@/lib/categoryHelpers";

interface BatchResult {
  url: string;
  status: 'pending' | 'processing' | 'success' | 'error' | 'duplicate';
  data?: any;
  error?: string;
  duplicatePost?: { title: string; slug: string };
  roadmapStatus?: 'pending' | 'generating' | 'success' | 'error' | 'skipped';
  roadmapError?: string;
}

interface BatchBlogGeneratorProps {
  onBlogGenerated: (data: any) => void;
}

export function BatchBlogGenerator({ onBlogGenerated }: BatchBlogGeneratorProps) {
  const [urls, setUrls] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [generateImages, setGenerateImages] = useState(true);
  const [generateRoadmaps, setGenerateRoadmaps] = useState(true);
  const { toast } = useToast();

  const generateAndSaveRoadmap = async (blogPostId: string, topic: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('Generating roadmap for:', topic);
      
      const { data, error } = await supabase.functions.invoke('generate-roadmap', {
        body: { topic, blogPostId }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Save roadmap to database
      const { error: saveError } = await supabase
        .from('roadmaps')
        .insert({
          blog_post_id: blogPostId,
          content: data.roadmap
        });

      if (saveError) {
        console.error('Error saving roadmap:', saveError);
        throw new Error('Failed to save roadmap to database');
      }

      console.log('Roadmap saved successfully for blog post:', blogPostId);
      return { success: true };
    } catch (error: any) {
      console.error('Error generating/saving roadmap:', error);
      return { success: false, error: error.message };
    }
  };

  const handleBatchGenerate = async () => {
    const urlList = urls
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urlList.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter at least one YouTube URL",
      });
      return;
    }

    if (urlList.length > 10) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Maximum 10 URLs can be processed at once",
      });
      return;
    }

    // Step 1: Validate for duplicates
    setIsValidating(true);
    const initialResults: BatchResult[] = [];
    const seenVideoIds = new Set<string>();

    for (let i = 0; i < urlList.length; i++) {
      const url = urlList[i];
      
      // Check against existing posts
      const { isDuplicate, existingPost } = await checkDuplicateVideo(supabase, url);
      
      if (isDuplicate && existingPost) {
        initialResults.push({
          url,
          status: 'duplicate',
          duplicatePost: { title: existingPost.title, slug: existingPost.slug },
          error: `Already exists: ${existingPost.title}`,
          roadmapStatus: 'skipped'
        });
        continue;
      }

      // Check for duplicates within the batch itself
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?\/]+)/)?.[1];
      if (videoId && seenVideoIds.has(videoId)) {
        initialResults.push({
          url,
          status: 'duplicate',
          error: 'Duplicate URL in this batch',
          roadmapStatus: 'skipped'
        });
        continue;
      }

      if (videoId) {
        seenVideoIds.add(videoId);
      }

      initialResults.push({ 
        url, 
        status: 'pending',
        roadmapStatus: generateRoadmaps ? 'pending' : 'skipped'
      });
    }

    setResults(initialResults);
    setIsValidating(false);

    // Count how many will actually be processed
    const validResults = initialResults.filter(r => r.status === 'pending');
    
    if (validResults.length === 0) {
      toast({
        variant: "destructive",
        title: "No videos to process",
        description: "All URLs are duplicates or invalid",
      });
      return;
    }

    if (validResults.length < urlList.length) {
      toast({
        title: "Duplicates detected",
        description: `${urlList.length - validResults.length} duplicate(s) will be skipped`,
      });
    }

    // Step 2: Process valid URLs
    setIsProcessing(true);

    for (let i = 0; i < initialResults.length; i++) {
      const result = initialResults[i];
      
      // Skip duplicates
      if (result.status === 'duplicate') {
        continue;
      }

      const url = result.url;
      
      // Update status to processing
      setResults((prev) =>
        prev.map((r, idx) =>
          idx === i ? { ...r, status: 'processing' } : r
        )
      );

      try {
        const { data, error } = await supabase.functions.invoke(
          'generate-blog-from-video',
          {
            body: { videoUrl: url, generateImage: generateImages },
          }
        );

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        // Update status to success for blog
        setResults((prev) =>
          prev.map((r, idx) =>
            idx === i ? { 
              ...r, 
              status: 'success', 
              data,
              roadmapStatus: generateRoadmaps ? 'generating' : 'skipped'
            } : r
          )
        );

        // Generate roadmap if enabled
        if (generateRoadmaps && data.title) {
          // First, we need to save the blog post to get its ID
          const { data: user } = await supabase.auth.getUser();
          if (!user?.user?.id) {
            throw new Error('User not authenticated');
          }

          // Create the blog post
          const slug = data.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

          let categoryId: string | null = null;
          const suggestedCategory = data.suggestedCategory?.trim();

          if (suggestedCategory) {
            try {
              const category = await getOrCreateCategoryByName(suggestedCategory);
              if (category) {
                categoryId = category.id;
              }
            } catch (catError: any) {
              console.error('Batch category auto-assign failed:', catError);
            }
          }

          const { data: savedPost, error: savePostError } = await supabase
            .from('blog_posts')
            .insert({
              title: data.title,
              slug: `${slug}-${Date.now()}`,
              content: data.content,
              excerpt: data.excerpt,
              featured_image: data.featuredImage || null,
              video_url: url,
              author_id: user.user.id,
              category_id: categoryId,
              status: 'draft'
            })
            .select()
            .single();

          if (savePostError) {
            console.error('Error saving blog post:', savePostError);
            setResults((prev) =>
              prev.map((r, idx) =>
                idx === i ? { 
                  ...r, 
                  roadmapStatus: 'error',
                  roadmapError: 'Failed to save blog post'
                } : r
              )
            );
          } else {
            // Now generate and save the roadmap
            const roadmapResult = await generateAndSaveRoadmap(savedPost.id, data.title);
            
            setResults((prev) =>
              prev.map((r, idx) =>
                idx === i ? { 
                  ...r, 
                  data: { ...data, savedPostId: savedPost.id },
                  roadmapStatus: roadmapResult.success ? 'success' : 'error',
                  roadmapError: roadmapResult.error
                } : r
              )
            );

            if (roadmapResult.success) {
              toast({
                title: "Roadmap Generated",
                description: `28-day roadmap created for "${data.title}"`,
              });
            }
          }
        }

        toast({
          title: "Success",
          description: `Blog ${i + 1}/${urlList.length} generated successfully`,
        });
      } catch (error: any) {
        console.error('Error generating blog:', error);
        
        // Update status to error
        setResults((prev) =>
          prev.map((r, idx) =>
            idx === i
              ? { ...r, status: 'error', error: error.message, roadmapStatus: 'skipped' }
              : r
          )
        );

        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to generate blog ${i + 1}: ${error.message}`,
        });
      }

      // Add a small delay between requests to avoid rate limiting
      if (i < initialResults.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    setIsProcessing(false);
    
    const successCount = results.filter(r => r.status === 'success').length;
    const duplicateCount = results.filter(r => r.status === 'duplicate').length;
    const roadmapSuccessCount = results.filter(r => r.roadmapStatus === 'success').length;
    
    toast({
      title: "Batch Processing Complete",
      description: `${successCount} blogs generated${generateRoadmaps ? `, ${roadmapSuccessCount} roadmaps created` : ''}, ${duplicateCount} duplicates skipped`,
    });
  };

  const handleUseGeneratedBlog = (data: any) => {
    onBlogGenerated(data);
    toast({
      title: "Blog Loaded",
      description: "The generated blog has been loaded into the form",
    });
  };

  const progress = results.length > 0
    ? (results.filter((r) => r.status === 'success' || r.status === 'error' || r.status === 'duplicate').length /
        results.length) *
      100
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Batch Blog Generator
        </CardTitle>
        <CardDescription>
          Generate multiple blog posts from YouTube videos at once (max 10 URLs)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="batch-urls">YouTube URLs (one per line)</Label>
          <Textarea
            id="batch-urls"
            placeholder="https://www.youtube.com/watch?v=example1&#10;https://www.youtube.com/watch?v=example2&#10;https://www.youtube.com/watch?v=example3"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={8}
            disabled={isProcessing}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Switch
              id="generate-images"
              checked={generateImages}
              onCheckedChange={setGenerateImages}
              disabled={isProcessing}
            />
            <Label htmlFor="generate-images" className="cursor-pointer">
              Generate custom AI images for each post
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="generate-roadmaps"
              checked={generateRoadmaps}
              onCheckedChange={setGenerateRoadmaps}
              disabled={isProcessing}
            />
            <Label htmlFor="generate-roadmaps" className="cursor-pointer flex items-center gap-2">
              <Map className="h-4 w-4" />
              Generate 28-Day Learning Roadmap for each post
            </Label>
          </div>
        </div>

        <Button
          onClick={handleBatchGenerate}
          disabled={isProcessing || isValidating || !urls.trim()}
          className="w-full"
        >
          {isValidating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Validating...
            </>
          ) : isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate All Blogs
            </>
          )}
        </Button>

        {results.length > 0 && (
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>Progress</Label>
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground">
                {results.filter((r) => r.status === 'success').length} of {results.length} completed
              </p>
            </div>

            <div className="space-y-2">
              <Label>Results</Label>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {results.map((result, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{result.url}</p>
                          {result.status === 'pending' && (
                            <p className="text-sm text-muted-foreground">Waiting...</p>
                          )}
                          {result.status === 'processing' && (
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Processing blog...
                            </p>
                          )}
                          {result.status === 'success' && (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Blog ✓
                                </span>
                                {result.roadmapStatus === 'generating' && (
                                  <span className="text-muted-foreground flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Roadmap...
                                  </span>
                                )}
                                {result.roadmapStatus === 'success' && (
                                  <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                    <Map className="h-3 w-3" />
                                    Roadmap ✓
                                  </span>
                                )}
                                {result.roadmapStatus === 'error' && (
                                  <span className="text-orange-600 dark:text-orange-400 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    Roadmap failed
                                  </span>
                                )}
                                {result.roadmapStatus === 'skipped' && generateRoadmaps && (
                                  <span className="text-muted-foreground text-xs">
                                    (no roadmap)
                                  </span>
                                )}
                              </div>
                              {result.data && (
                                <p className="text-sm font-medium">{result.data.title}</p>
                              )}
                              {result.roadmapError && (
                                <p className="text-xs text-orange-600 dark:text-orange-400">
                                  {result.roadmapError}
                                </p>
                              )}
                            </div>
                          )}
                          {result.status === 'error' && (
                            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                              <XCircle className="h-3 w-3" />
                              {result.error || 'Failed'}
                            </p>
                          )}
                          {result.status === 'duplicate' && (
                            <div className="space-y-1">
                              <p className="text-sm text-orange-600 dark:text-orange-400 flex items-center gap-2">
                                <AlertCircle className="h-3 w-3" />
                                Duplicate - Skipped
                              </p>
                              {result.duplicatePost && (
                                <p className="text-xs text-muted-foreground">
                                  Existing post: {result.duplicatePost.title}
                                </p>
                              )}
                              {result.error && !result.duplicatePost && (
                                <p className="text-xs text-muted-foreground">{result.error}</p>
                              )}
                            </div>
                          )}
                        </div>
                        {result.status === 'success' && result.data && !result.data.savedPostId && (
                          <Button
                            size="sm"
                            onClick={() => handleUseGeneratedBlog(result.data)}
                          >
                            Use This
                          </Button>
                        )}
                        {result.status === 'success' && result.data?.savedPostId && (
                          <span className="text-xs text-green-600 dark:text-green-400">
                            Saved as draft
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
